import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, Users, X } from "lucide-react";
import { searchChannels } from "@/lib/api";
import { formatCompact } from "@/lib/format";

// Same rule as Analyze.jsx — if the query looks like a URL/handle/ID we
// let it pass through; otherwise we treat it as a name and show a picker.
const isDirectChannelRef = (q) => {
  if (!q) return false;
  const s = q.trim();
  return (
    s.includes("youtube.com/") ||
    s.includes("youtu.be/") ||
    /^UC[\w-]{20,}$/.test(s) ||
    s.startsWith("@")
  );
};

/**
 * Reusable channel search input that supports both:
 *  - direct channel URLs / @handles / channel IDs (typed and Enter)
 *  - free-text channel names (shows a picker with matching channels)
 *
 * onResolve(channelUrl) is called whenever the parent should treat
 * `channelUrl` as the final resolved reference (a full youtube.com URL).
 */
export default function ChannelSearchInput({
  value,
  onChange,
  onResolve,
  placeholder = "Channel URL or name",
  testId = "channel-search",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const searchable = useMemo(
    () => !isDirectChannelRef(value) && (value || "").trim().length >= 2,
    [value],
  );

  useEffect(() => {
    if (!searchable) {
      setSuggestions([]);
      setShowPicker(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchChannels(value.trim());
        setSuggestions(data.results || []);
        setShowPicker(true);
      } catch (e) {
        console.debug("channel search failed", e);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [value, searchable]);

  // Close the picker if the user clicks outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setShowPicker(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (c) => {
    onChange(c.title);
    setShowPicker(false);
    onResolve?.(`https://youtube.com/channel/${c.channel_id}`, c);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-2 px-3 h-12 rounded-xl border border-border bg-card">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <Input
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => searchable && setShowPicker(true)}
          placeholder={placeholder}
          className="border-0 shadow-none focus-visible:ring-0 h-11 bg-transparent px-0"
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            aria-label="Clear channel input"
            data-testid={`${testId}-clear`}
            onClick={() => {
              onChange("");
              setSuggestions([]);
              setShowPicker(false);
            }}
            className="p-1 rounded hover:bg-secondary/60 text-muted-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showPicker && searchable && (
        <div
          data-testid={`${testId}-picker`}
          className="absolute z-40 left-0 right-0 mt-2 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {loading && suggestions.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Searching channels…</div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">
              No channels matched "<span className="font-semibold">{value}</span>".
            </div>
          )}
          {suggestions.length > 0 && (
            <>
              <div className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border/60">
                {suggestions.length} matches — click to select
              </div>
              <ul className="max-h-[360px] overflow-auto divide-y divide-border/50">
                {suggestions.map((c) => (
                  <li key={c.channel_id}>
                    <button
                      type="button"
                      data-testid={`${testId}-option-${c.channel_id}`}
                      onClick={() => pick(c)}
                      className="w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors flex items-center gap-3"
                    >
                      {c.thumbnail ? (
                        <img
                          src={c.thumbnail}
                          alt={`${c.title} avatar`}
                          className="w-10 h-10 rounded-full object-cover shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-secondary shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{c.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.handle && <span className="font-mono">{c.handle}</span>}
                          {c.handle && c.description && " · "}
                          {c.description}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono flex items-center gap-1 text-muted-foreground justify-end">
                          <Users className="w-3 h-3" />
                          {c.hidden_subscribers ? "hidden" : formatCompact(c.subscribers)}
                        </div>
                        {c.country && (
                          <div className="text-[10px] text-muted-foreground/70 font-mono mt-0.5">
                            {c.country}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export { isDirectChannelRef };
